'use client'

import { useRef, useState, useEffect, useCallback } from 'react'

// ===== Status Messages =====
const STATUS: Record<string, string[]> = {
  idle: [
    'scanning for human...',
    'still looking... are you there?',
    '*stares patiently*',
    'i can see you. you know that, right?',
    'blink if you need help.',
    'hello? the login form is right there.',
    "i don't have all day. well, i do. but still.",
    'fun fact: i was an eye before they gave me a body.',
    "you're just going to stare at me? ok cool.",
    '*practices looking casual*',
  ],
  emailFocus: [
    "ooh, they're typing! act natural.",
    'reading... wait, am i allowed to read this?',
    "that's a nice email you got there.",
    'interesting domain. very professional.',
    "i'm going to pretend i didn't memorize that.",
  ],
  emailTyping: [
    "yep, that's definitely letters.",
    "keep going, you're doing great.",
    'almost looks like a real email...',
    'the @ was a nice touch.',
    'ooh, a dot! fancy.',
    'gmail? outlook? hotmail?? ...no judgement.',
  ],
  passwordFocus: [
    "i'm not looking. promise.",
    '*definitely not peeking*',
    "la la la can't see anything.",
    'is it "password123"? just guessing.',
    'i have fingers over my eyes. this is fine.',
    'your secret is safe with me. mostly.',
  ],
  passwordTyping: [
    "i'm not looking i'm not looking i'm n—",
    "wow that's a lot of dots.",
    'please be longer than 4 characters.',
    'strong password energy. probably.',
    "i can hear the keyboard but i definitely can't see.",
    'that sounds like a very secure password.',
  ],
  passwordShow: [
    'AHHH! YOUR PASSWORD! I SAW NOTHING!',
    'delete my memory. immediately.',
    '*frantically covering eyes*',
    'this is a violation of... something!',
    'WHY WOULD YOU DO THAT.',
    'i am looking RESPECTFULLY away.',
  ],
  loading: [
    'verifying human-ness...',
    "checking if you're sentient...",
    'consulting the authentication oracle...',
    'asking the servers nicely...',
  ],
  success: [
    'human confirmed. welcome back, friend.',
  ],
  error: [
    "that wasn't it, chief.",
    'oof. try again?',
    'the oracle says no.',
    'have you tried being a different person?',
    "wrong password. i didn't peek, i just know.",
    'the vibes are off on that one.',
  ],
}

function randomFrom(arr: string[]) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export type CharacterState = 'idle' | 'email' | 'password' | 'loading' | 'success' | 'error'

interface HmnCharacterProps {
  formState: CharacterState
  passwordVisible: boolean
  emailValue: string
}

export default function HmnCharacter({ formState, passwordVisible, emailValue }: HmnCharacterProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [statusText, setStatusText] = useState('scanning for human...')
  const [statusClass, setStatusClass] = useState('')
  const prevStateRef = useRef<string>('idle')
  const prevPwVisRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof setInterval>>()
  const blinkTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const breathPhaseRef = useRef(0)
  const rafRef = useRef<number>()

  // Refs for SVG elements
  const headGroupRef = useRef<SVGGElement>(null)
  const faceRef = useRef<SVGGElement>(null)
  const lIrisRef = useRef<SVGCircleElement>(null)
  const rIrisRef = useRef<SVGCircleElement>(null)
  const lPupilRef = useRef<SVGCircleElement>(null)
  const rPupilRef = useRef<SVGCircleElement>(null)
  const lHlRef = useRef<SVGCircleElement>(null)
  const rHlRef = useRef<SVGCircleElement>(null)
  const lLidRef = useRef<SVGEllipseElement>(null)
  const rLidRef = useRef<SVGEllipseElement>(null)
  const lBrowRef = useRef<SVGPathElement>(null)
  const rBrowRef = useRef<SVGPathElement>(null)
  const mouthRef = useRef<SVGPathElement>(null)
  const lBlushRef = useRef<SVGEllipseElement>(null)
  const rBlushRef = useRef<SVGEllipseElement>(null)
  const coverHandsRef = useRef<SVGGElement>(null)
  const peekHandsRef = useRef<SVGGElement>(null)
  const sweatDropRef = useRef<SVGGElement>(null)
  const bodyGroupRef = useRef<SVGGElement>(null)
  const leftArmPathRef = useRef<SVGPathElement>(null)
  const rightArmPathRef = useRef<SVGPathElement>(null)
  const leftHandRef = useRef<SVGCircleElement>(null)
  const rightHandRef = useRef<SVGCircleElement>(null)

  // ── Helpers ──
  const setAttr = (el: SVGElement | null, cx: number, cy: number) => {
    if (!el) return
    el.setAttribute('cx', String(cx))
    el.setAttribute('cy', String(cy))
  }

  const setStatus = useCallback((category: string, cls = '') => {
    const msgs = STATUS[category]
    if (msgs) {
      setStatusText(randomFrom(msgs))
      setStatusClass(cls)
    }
  }, [])

  // ── Eye tracking ──
  const trackEyes = useCallback((mx: number, my: number) => {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const svgX = ((mx - rect.left) / rect.width) * 220
    const svgY = ((my - rect.top) / rect.height) * 180
    const maxOff = 5

    for (const [iris, pupil, hl, cx, cy] of [
      [lIrisRef.current, lPupilRef.current, lHlRef.current, 95, 70],
      [rIrisRef.current, rPupilRef.current, rHlRef.current, 125, 70],
    ] as [SVGCircleElement | null, SVGCircleElement | null, SVGCircleElement | null, number, number][]) {
      const dx = svgX - cx, dy = svgY - cy
      const dist = Math.sqrt(dx * dx + dy * dy)
      const a = Math.atan2(dy, dx)
      const s = Math.min(dist / 50, 1)
      const ox = Math.cos(a) * maxOff * s
      const oy = Math.sin(a) * maxOff * s
      setAttr(iris, cx + ox, cy + oy)
      setAttr(pupil, cx + ox, cy + oy)
      setAttr(hl, cx + ox + 2, cy + oy - 2)
    }
  }, [])

  const tiltHead = useCallback((mx: number, my: number) => {
    const svg = svgRef.current
    const head = headGroupRef.current
    if (!svg || !head) return
    const rect = svg.getBoundingClientRect()
    const dx = (mx - rect.left - rect.width / 2) / rect.width
    const dy = (my - rect.top - rect.height / 2) / rect.height
    head.style.transition = 'transform 0.15s ease-out'
    head.style.transformOrigin = '110px 80px'
    head.style.transform = `rotate(${dx * 6}deg) translateY(${dy * 4}px)`
  }, [])

  // ── Blink ──
  const doBlink = useCallback(() => {
    const lLid = lLidRef.current, rLid = rLidRef.current
    if (!lLid || !rLid) return
    lLid.setAttribute('ry', '15')
    rLid.setAttribute('ry', '15')
    setTimeout(() => {
      lLid.setAttribute('ry', '0')
      rLid.setAttribute('ry', '0')
    }, 100)
  }, [])

  // ── Arm positions ──
  const armsRest = useCallback(() => {
    leftArmPathRef.current?.setAttribute('d', 'M88,118 C78,124 68,136 64,148')
    leftHandRef.current?.setAttribute('cx', '64')
    leftHandRef.current?.setAttribute('cy', '148')
    rightArmPathRef.current?.setAttribute('d', 'M132,118 C142,124 152,136 156,148')
    rightHandRef.current?.setAttribute('cx', '156')
    rightHandRef.current?.setAttribute('cy', '148')
  }, [])

  const armsUp = useCallback(() => {
    leftArmPathRef.current?.setAttribute('d', 'M88,118 C72,108 68,88 78,68')
    leftHandRef.current?.setAttribute('cx', '78')
    leftHandRef.current?.setAttribute('cy', '68')
    rightArmPathRef.current?.setAttribute('d', 'M132,118 C148,108 152,88 142,68')
    rightHandRef.current?.setAttribute('cx', '142')
    rightHandRef.current?.setAttribute('cy', '68')
  }, [])

  const armsWave = useCallback(() => {
    const frames = [
      { d: 'M132,118 C148,108 160,90 162,74', cx: 162, cy: 74 },
      { d: 'M132,118 C148,108 160,84 158,68', cx: 158, cy: 68 },
      { d: 'M132,118 C148,108 160,90 162,74', cx: 162, cy: 74 },
      { d: 'M132,118 C148,108 160,84 158,68', cx: 158, cy: 68 },
      { d: 'M132,118 C142,124 152,136 156,148', cx: 156, cy: 148 },
    ]
    let i = 0
    const step = () => {
      if (i >= frames.length) return
      const rArmPath = rightArmPathRef.current
      const rHand = rightHandRef.current
      if (rArmPath) {
        rArmPath.style.transition = 'all 0.12s ease-in-out'
        rArmPath.setAttribute('d', frames[i].d)
      }
      if (rHand) {
        rHand.style.transition = 'all 0.12s ease-in-out'
        rHand.setAttribute('cx', String(frames[i].cx))
        rHand.setAttribute('cy', String(frames[i].cy))
      }
      i++
      setTimeout(step, 140)
    }
    step()
  }, [])

  // ── Head shake ──
  const headShake = useCallback((baseY = 0) => {
    const h = headGroupRef.current
    if (!h) return
    const seq = [5, -5, 4, -4, 2, -2, 0]
    let i = 0
    const go = () => {
      if (i >= seq.length) return
      h.style.transition = 'transform 0.07s ease-in-out'
      h.style.transform = `translateY(${baseY}px) rotate(${seq[i]}deg)`
      i++
      setTimeout(go, 70)
    }
    go()
  }, [])

  // ── Bounce ──
  const bounce = useCallback(() => {
    const h = headGroupRef.current
    if (!h) return
    const frames = [
      { y: -10, d: 100 }, { y: 0, d: 120 },
      { y: -6, d: 100 }, { y: 0, d: 120 },
      { y: -3, d: 80 }, { y: 0, d: 100 },
    ]
    let i = 0
    const go = () => {
      if (i >= frames.length) return
      h.style.transition = `transform ${frames[i].d}ms cubic-bezier(.4,0,.2,1)`
      h.style.transform = `translateY(${frames[i].y}px)`
      i++
      setTimeout(go, frames[i - 1].d)
    }
    go()
  }, [])

  // ── Visibility helpers ──
  const showFace = useCallback(() => {
    const f = faceRef.current
    if (f) { f.style.transition = 'opacity 0.3s'; f.style.opacity = '1' }
  }, [])
  const hideCover = useCallback(() => {
    const c = coverHandsRef.current
    if (c) { c.style.transition = 'opacity 0.3s'; c.style.opacity = '0' }
  }, [])
  const hidePeek = useCallback(() => {
    const p = peekHandsRef.current
    if (p) { p.style.transition = 'opacity 0.3s'; p.style.opacity = '0' }
  }, [])
  const hideSweat = useCallback(() => {
    const s = sweatDropRef.current
    if (s) { s.style.transition = 'opacity 0.3s'; s.style.opacity = '0' }
  }, [])
  const resetExpression = useCallback(() => {
    lBrowRef.current?.setAttribute('d', 'M82,54 Q95,49 108,54')
    rBrowRef.current?.setAttribute('d', 'M112,54 Q125,49 138,54')
    mouthRef.current?.setAttribute('d', 'M100,92 Q110,99 120,92')
    if (lBlushRef.current) lBlushRef.current.style.opacity = '0'
    if (rBlushRef.current) rBlushRef.current.style.opacity = '0'
    lLidRef.current?.setAttribute('ry', '0')
    rLidRef.current?.setAttribute('ry', '0')
  }, [])

  // ── State-driven animation ──
  useEffect(() => {
    const stateKey = passwordVisible ? `${formState}:pwVisible` : formState
    const prevKey = prevPwVisRef.current ? `${prevStateRef.current}:pwVisible` : prevStateRef.current

    if (stateKey === prevKey) {
      // Only handle email typing updates
      if (formState === 'email') {
        const progress = Math.min(emailValue.length / 25, 1)
        const ox = -4 + progress * 8
        for (const [iris, pupil, hl, cx, cy] of [
          [lIrisRef.current, lPupilRef.current, lHlRef.current, 95, 70],
          [rIrisRef.current, rPupilRef.current, rHlRef.current, 125, 70],
        ] as [SVGCircleElement | null, SVGCircleElement | null, SVGCircleElement | null, number, number][]) {
          if (iris) iris.style.transition = 'all 0.08s'
          if (pupil) pupil.style.transition = 'all 0.08s'
          if (hl) hl.style.transition = 'all 0.08s'
          setAttr(iris, cx + ox, cy + 4)
          setAttr(pupil, cx + ox, cy + 4)
          setAttr(hl, cx + ox + 2, cy + 2)
        }
        if (emailValue.length === 1) setStatus('emailTyping', 'active')
        if (emailValue.includes('@') && !emailValue.includes('@.')) {
          setStatusText('the @ was a nice touch.')
          setStatusClass('active')
        }
      }
      return
    }

    prevStateRef.current = formState
    prevPwVisRef.current = passwordVisible

    // Handle password visibility toggle within password state
    if (formState === 'password' && passwordVisible) {
      // Cover eyes fully
      hidePeek()
      if (faceRef.current) { faceRef.current.style.transition = 'opacity 0.3s'; faceRef.current.style.opacity = '0' }
      const cover = coverHandsRef.current
      if (cover) { cover.style.transition = 'opacity 0.25s ease 0.1s'; cover.style.opacity = '1' }
      const sweat = sweatDropRef.current
      if (sweat) { sweat.style.transition = 'opacity 0.3s'; sweat.style.opacity = '1' }
      mouthRef.current?.setAttribute('d', 'M103,95 Q110,90 117,95')
      headShake(4)
      setStatus('passwordShow', 'danger')
      return
    }

    if (formState === 'password' && !passwordVisible) {
      // Peek through fingers
      hideCover()
      hideSweat()
      armsUp()
      setTimeout(() => {
        if (faceRef.current) { faceRef.current.style.transition = 'opacity 0.3s'; faceRef.current.style.opacity = '0' }
        const peek = peekHandsRef.current
        if (peek) { peek.style.transition = 'opacity 0.35s ease 0.1s'; peek.style.opacity = '1' }
      }, 200)
      lBrowRef.current?.setAttribute('d', 'M82,52 Q95,48 108,54')
      rBrowRef.current?.setAttribute('d', 'M112,54 Q125,48 138,52')
      mouthRef.current?.setAttribute('d', 'M104,92 Q110,96 116,92')
      const h = headGroupRef.current
      if (h) {
        h.style.transition = 'transform 0.5s ease'
        h.style.transform = 'translateY(4px) rotate(-3deg)'
      }
      if (lBlushRef.current) lBlushRef.current.style.opacity = '0'
      if (rBlushRef.current) rBlushRef.current.style.opacity = '0'
      setStatus('passwordFocus', 'warn')
      return
    }

    // Full state transitions
    switch (formState) {
      case 'idle': {
        showFace(); hideCover(); hidePeek(); hideSweat(); armsRest(); resetExpression()
        const h = headGroupRef.current
        if (h) { h.style.transition = 'transform 0.5s ease'; h.style.transform = 'translateY(0) rotate(0)' }
        for (const [iris, pupil, hl, cx, cy] of [
          [lIrisRef.current, lPupilRef.current, lHlRef.current, 95, 70],
          [rIrisRef.current, rPupilRef.current, rHlRef.current, 125, 70],
        ] as [SVGCircleElement | null, SVGCircleElement | null, SVGCircleElement | null, number, number][]) {
          if (iris) iris.style.transition = 'all 0.4s ease-out'
          if (pupil) pupil.style.transition = 'all 0.4s ease-out'
          if (hl) hl.style.transition = 'all 0.4s ease-out'
          setAttr(iris, cx, cy); setAttr(pupil, cx, cy); setAttr(hl, cx + 2, cy - 2)
        }
        setStatus('idle')
        break
      }

      case 'email': {
        showFace(); hideCover(); hidePeek(); hideSweat()
        const h = headGroupRef.current
        if (h) { h.style.transition = 'transform 0.4s ease'; h.style.transform = 'translateY(4px)' }
        lBrowRef.current?.setAttribute('d', 'M82,50 Q95,44 108,50')
        rBrowRef.current?.setAttribute('d', 'M112,50 Q125,44 138,50')
        mouthRef.current?.setAttribute('d', 'M102,92 Q110,98 118,92')
        if (lBlushRef.current) { lBlushRef.current.style.transition = 'opacity 0.4s'; lBlushRef.current.style.opacity = '0.4' }
        if (rBlushRef.current) { rBlushRef.current.style.transition = 'opacity 0.4s'; rBlushRef.current.style.opacity = '0.4' }
        setStatus('emailFocus', 'active')
        break
      }

      case 'loading': {
        showFace(); hideCover(); hidePeek(); hideSweat()
        lLidRef.current?.setAttribute('ry', '7')
        rLidRef.current?.setAttribute('ry', '7')
        mouthRef.current?.setAttribute('d', 'M96,90 Q110,106 124,90')
        lBrowRef.current?.setAttribute('d', 'M82,46 Q95,40 108,46')
        rBrowRef.current?.setAttribute('d', 'M112,46 Q125,40 138,46')
        if (lBlushRef.current) { lBlushRef.current.style.transition = 'opacity 0.3s'; lBlushRef.current.style.opacity = '0.6' }
        if (rBlushRef.current) { rBlushRef.current.style.transition = 'opacity 0.3s'; rBlushRef.current.style.opacity = '0.6' }
        armsWave()
        bounce()
        setStatus('loading', 'active')
        break
      }

      case 'success': {
        setStatus('success', 'success')
        break
      }

      case 'error': {
        showFace(); hideCover(); hidePeek(); armsRest()
        lBrowRef.current?.setAttribute('d', 'M82,50 Q95,56 108,52')
        rBrowRef.current?.setAttribute('d', 'M112,52 Q125,56 138,50')
        mouthRef.current?.setAttribute('d', 'M100,97 Q110,91 120,97')
        const h = headGroupRef.current
        if (h) { h.style.transition = 'transform 0.5s ease'; h.style.transform = 'translateY(6px) rotate(-4deg)' }
        if (lBlushRef.current) lBlushRef.current.style.opacity = '0'
        if (rBlushRef.current) rBlushRef.current.style.opacity = '0'
        const sweat = sweatDropRef.current
        if (sweat) { sweat.style.transition = 'opacity 0.3s'; sweat.style.opacity = '0.7' }
        setTimeout(() => headShake(6), 300)
        setStatus('error', 'danger')
        break
      }
    }
  }, [formState, passwordVisible, emailValue, setStatus, showFace, hideCover, hidePeek, hideSweat, armsRest, armsUp, armsWave, resetExpression, headShake, bounce])

  // ── Password typing status updates ──
  useEffect(() => {
    if (formState !== 'password') return
    if (!passwordVisible) {
      if (Math.random() < 0.4) setStatus('passwordTyping', 'warn')
    }
  // Only trigger on password typing, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState])

  // ── Mouse tracking ──
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formState === 'idle' || formState === 'email') {
        trackEyes(e.clientX, e.clientY)
        tiltHead(e.clientX, e.clientY)
      }
    }
    document.addEventListener('mousemove', handler)
    return () => document.removeEventListener('mousemove', handler)
  }, [formState, trackEyes, tiltHead])

  // ── Blink loop ──
  useEffect(() => {
    const startBlink = () => {
      blinkTimerRef.current = setTimeout(() => {
        if (formState !== 'password' || passwordVisible) {
          // skip blink when peeking/covering
        }
        if (formState === 'idle' || formState === 'email' || formState === 'error') {
          doBlink()
          if (Math.random() < 0.2) setTimeout(doBlink, 250) // double blink
        }
        startBlink()
      }, 2000 + Math.random() * 3000)
    }
    startBlink()
    return () => { if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current) }
  }, [formState, doBlink, passwordVisible])

  // ── Breathing ──
  useEffect(() => {
    const go = () => {
      breathPhaseRef.current += 0.025
      const off = Math.sin(breathPhaseRef.current) * 2
      if (bodyGroupRef.current) {
        bodyGroupRef.current.style.transform = `translateY(${off}px)`
      }
      rafRef.current = requestAnimationFrame(go)
    }
    rafRef.current = requestAnimationFrame(go)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [])

  // ── Idle chatter ──
  useEffect(() => {
    idleTimerRef.current = setInterval(() => {
      if (formState === 'idle') setStatus('idle')
    }, 4500)
    return () => { if (idleTimerRef.current) clearInterval(idleTimerRef.current) }
  }, [formState, setStatus])

  // Status color class — cyan for idle/email, amber for password peeking, red for password exposed, green for success
  const statusColorMap: Record<string, string> = {
    '': 'text-[var(--hg-cyan-text,#61d8fe)]',
    active: 'text-[var(--hg-cyan-text,#61d8fe)]',
    warn: 'text-[#f59e0b]',
    danger: 'text-[var(--hg-danger,#f87171)]',
    success: 'text-[var(--hg-success,#34d399)]',
  }

  return (
    <div className="flex flex-col items-center mb-4">
      {/* Character SVG */}
      <div className="w-[180px] h-[150px] relative">
        <svg ref={svgRef} viewBox="0 0 220 180" className="w-full h-full overflow-visible" xmlns="http://www.w3.org/2000/svg">
          {/* Body */}
          <g ref={bodyGroupRef}>
            <path d="M88,120 C88,112 96,108 110,108 C124,108 132,112 132,120 L128,152 C128,156 120,158 110,158 C100,158 92,156 92,152 Z"
                  fill="rgba(84,74,232,0.12)" stroke="rgba(84,74,232,0.3)" strokeWidth="1.5"/>
            <g>
              <path ref={leftArmPathRef} d="M88,118 C78,124 68,136 64,148"
                    fill="none" stroke="rgba(84,74,232,0.35)" strokeWidth="3" strokeLinecap="round"/>
              <circle ref={leftHandRef} cx="64" cy="148" r="5" fill="rgba(84,74,232,0.15)" stroke="rgba(84,74,232,0.35)" strokeWidth="1.5"/>
            </g>
            <g>
              <path ref={rightArmPathRef} d="M132,118 C142,124 152,136 156,148"
                    fill="none" stroke="rgba(84,74,232,0.35)" strokeWidth="3" strokeLinecap="round"/>
              <circle ref={rightHandRef} cx="156" cy="148" r="5" fill="rgba(84,74,232,0.15)" stroke="rgba(84,74,232,0.35)" strokeWidth="1.5"/>
            </g>
          </g>

          {/* Head */}
          <g ref={headGroupRef}>
            <ellipse cx="110" cy="75" rx="40" ry="44"
                     fill="rgba(84,74,232,0.08)" stroke="rgba(84,74,232,0.25)" strokeWidth="1.5"/>

            <g ref={faceRef}>
              {/* Left Eye */}
              <g>
                <ellipse cx="95" cy="70" rx="12" ry="14" fill="#f0eef8" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8"/>
                <circle ref={lIrisRef} cx="95" cy="70" r="6" fill="#544ae8"/>
                <circle ref={lPupilRef} cx="95" cy="70" r="2.8" fill="#1a1625"/>
                <circle ref={lHlRef} cx="97.5" cy="67.5" r="1.3" fill="rgba(255,255,255,0.85)"/>
                <ellipse ref={lLidRef} cx="95" cy="70" rx="13" ry="0" fill="var(--hg-bg-primary, #1d212a)"/>
              </g>
              {/* Right Eye */}
              <g>
                <ellipse cx="125" cy="70" rx="12" ry="14" fill="#f0eef8" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8"/>
                <circle ref={rIrisRef} cx="125" cy="70" r="6" fill="#544ae8"/>
                <circle ref={rPupilRef} cx="125" cy="70" r="2.8" fill="#1a1625"/>
                <circle ref={rHlRef} cx="127.5" cy="67.5" r="1.3" fill="rgba(255,255,255,0.85)"/>
                <ellipse ref={rLidRef} cx="125" cy="70" rx="13" ry="0" fill="var(--hg-bg-primary, #1d212a)"/>
              </g>
              {/* Brows */}
              <path ref={lBrowRef} d="M82,54 Q95,49 108,54" fill="none" stroke="rgba(84,74,232,0.45)" strokeWidth="2.5" strokeLinecap="round"/>
              <path ref={rBrowRef} d="M112,54 Q125,49 138,54" fill="none" stroke="rgba(84,74,232,0.45)" strokeWidth="2.5" strokeLinecap="round"/>
              {/* Mouth */}
              <path ref={mouthRef} d="M100,92 Q110,99 120,92" fill="none" stroke="rgba(84,74,232,0.35)" strokeWidth="2" strokeLinecap="round"/>
              {/* Blush */}
              <ellipse ref={lBlushRef} cx="84" cy="83" rx="7" ry="3.5" fill="rgba(244,114,182,0.2)" opacity="0"/>
              <ellipse ref={rBlushRef} cx="136" cy="83" rx="7" ry="3.5" fill="rgba(244,114,182,0.2)" opacity="0"/>
            </g>

            {/* Cover hands */}
            <g ref={coverHandsRef} opacity="0">
              <ellipse cx="95" cy="70" rx="15" ry="12" fill="rgba(84,74,232,0.2)" stroke="rgba(84,74,232,0.4)" strokeWidth="1.5"/>
              <ellipse cx="125" cy="70" rx="15" ry="12" fill="rgba(84,74,232,0.2)" stroke="rgba(84,74,232,0.4)" strokeWidth="1.5"/>
              <path d="M83,67 Q81,70 83,73 M87,65 Q85,70 87,75" fill="none" stroke="rgba(84,74,232,0.3)" strokeWidth="1"/>
              <path d="M137,67 Q139,70 137,73 M133,65 Q135,70 133,75" fill="none" stroke="rgba(84,74,232,0.3)" strokeWidth="1"/>
            </g>

            {/* Peek hands */}
            <g ref={peekHandsRef} opacity="0">
              <path d="M82,60 Q80,70 82,80" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <path d="M88,58 Q86,70 88,82" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <path d="M100,58 Q102,70 100,82" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <path d="M120,58 Q118,70 120,82" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <path d="M132,58 Q134,70 132,82" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <path d="M138,60 Q140,70 138,80" fill="none" stroke="rgba(84,74,232,0.4)" strokeWidth="3" strokeLinecap="round"/>
              <circle cx="94" cy="71" r="4.5" fill="#544ae8"/>
              <circle cx="94" cy="71" r="2" fill="#1a1625"/>
              <circle cx="95.5" cy="69" r="0.8" fill="rgba(255,255,255,0.8)"/>
              <circle cx="126" cy="71" r="4.5" fill="#544ae8"/>
              <circle cx="126" cy="71" r="2" fill="#1a1625"/>
              <circle cx="127.5" cy="69" r="0.8" fill="rgba(255,255,255,0.8)"/>
            </g>

            {/* Sweat drop */}
            <g ref={sweatDropRef} opacity="0">
              <path d="M142,56 Q144,50 143,44" fill="none" stroke="rgba(97,216,254,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="143" cy="43" r="2" fill="rgba(97,216,254,0.3)"/>
            </g>
          </g>
        </svg>
      </div>

      {/* Status line */}
      <p className={`text-center text-[13px] italic min-h-[20px] transition-colors duration-300 font-diatype ${statusColorMap[statusClass] || statusColorMap['']}`}>
        {statusText}
      </p>
    </div>
  )
}
